/**
 * Pictorial dashboard charts.
 *
 * Small, dependency-free SVG charts used by the three role dashboards so the
 * numbers read at a glance instead of as rows of text. Every chart:
 *   - takes colours as CSS custom properties (themes + dark mode keep working),
 *   - scales with its container (viewBox + width 100%),
 *   - draws nothing for zero — an empty dataset shows a short note instead,
 *   - keeps the exact number visible (label or legend), so nothing is lost
 *     compared to the text version it replaces.
 */

const num = (v) => Number(v) || 0;

function Empty({ children = 'Nothing to show for this period.' }) {
  return <div className="mono text-xs text-muted" style={{ padding: '12px 0' }}>{children}</div>;
}

function Legend({ items, total }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: 1 }}>
      {items.map((s) => (
        <div key={s.label} className="flex-between" style={{ fontSize: 12, gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', minWidth: 0 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flex: '0 0 auto' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</span>
          </span>
          <span className="mono" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {num(s.value)}
            {total > 0 && <span className="text-muted" style={{ fontWeight: 400 }}> · {Math.round((num(s.value) / total) * 100)}%</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Donut with a legend on the right.
 * segments: [{ label, value, color }]
 */
export function DonutChart({ segments = [], centerLabel = 'total', size = 120, hideZero = false }) {
  const items = hideZero ? segments.filter((s) => num(s.value) > 0) : segments;
  const total = items.reduce((a, s) => a + num(s.value), 0);
  const r = 42, stroke = 14, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg viewBox="0 0 100 100" width={size} height={size} role="img"
        aria-label={items.map((s) => `${s.label} ${num(s.value)}`).join(', ')} style={{ flex: '0 0 auto' }}>
        <circle cx="50" cy="50" r={r} fill="none" strokeWidth={stroke} style={{ stroke: 'var(--border-subtle)' }} />
        {total > 0 && items.map((s) => {
          const len = (num(s.value) / total) * c;
          const el = (
            <circle key={s.label} cx="50" cy="50" r={r} fill="none" strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset}
              transform="rotate(-90 50 50)" style={{ stroke: s.color }}>
              <title>{`${s.label}: ${num(s.value)}`}</title>
            </circle>
          );
          offset += len;
          return el;
        })}
        <text x="50" y="50" textAnchor="middle" fontSize="20" fontWeight="700" style={{ fill: 'var(--text-primary)' }}>{total}</text>
        <text x="50" y="64" textAnchor="middle" fontSize="8" style={{ fill: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>{centerLabel}</text>
      </svg>
      <Legend items={items} total={total} />
    </div>
  );
}

/**
 * Vertical columns with the value above each bar.
 * bars: [{ label, value, color }]
 */
export function ColumnChart({ bars = [], height = 150 }) {
  if (!bars.length) return <Empty />;
  const W = 100 * bars.length, H = height, top = 18, bottom = 30;
  const max = Math.max(1, ...bars.map((b) => num(b.value)));
  const slot = W / bars.length, bw = Math.min(56, slot * 0.55);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="xMidYMid meet" role="img"
      aria-label={bars.map((b) => `${b.label} ${num(b.value)}`).join(', ')}>
      <line x1="0" x2={W} y1={H - bottom} y2={H - bottom} style={{ stroke: 'var(--border-subtle)' }} />
      {bars.map((b, i) => {
        const v = num(b.value);
        const h = ((H - top - bottom) * v) / max;
        const x = i * slot + (slot - bw) / 2;
        const y = H - bottom - h;
        return (
          <g key={b.label}>
            {v > 0 && <rect x={x} y={y} width={bw} height={h} rx="4" style={{ fill: b.color || 'var(--accent-primary)' }}><title>{`${b.label}: ${v}`}</title></rect>}
            <text x={x + bw / 2} y={y - 5} textAnchor="middle" fontSize="13" fontWeight="700" style={{ fill: 'var(--text-primary)' }}>{v}</text>
            <text x={i * slot + slot / 2} y={H - 10} textAnchor="middle" fontSize="11" style={{ fill: 'var(--text-tertiary)' }}>{b.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Circular progress ring for a 0..1 ratio (null → "—"). */
export function RingGauge({ value, label, color = 'var(--accent-primary)', size = 96 }) {
  const has = value != null && !Number.isNaN(Number(value));
  const v = has ? Math.max(0, Math.min(1, Number(value))) : 0;
  const r = 40, c = 2 * Math.PI * r;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={`${label}: ${has ? Math.round(v * 100) + '%' : 'no data'}`}>
        <circle cx="50" cy="50" r={r} fill="none" strokeWidth="10" style={{ stroke: 'var(--border-subtle)' }} />
        {v > 0 && (
          <circle cx="50" cy="50" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${v * c} ${c}`} transform="rotate(-90 50 50)" style={{ stroke: color }} />
        )}
        <text x="50" y="57" textAnchor="middle" fontSize="20" fontWeight="700" style={{ fill: 'var(--text-primary)' }}>
          {has ? `${Math.round(v * 100)}%` : '—'}
        </text>
      </svg>
      <div className="mono text-xs text-muted" style={{ textAlign: 'center' }}>{label}</div>
    </div>
  );
}

/**
 * A real funnel: stacked trapezoids whose width follows the count.
 * stages: [{ key, label, count, color }]
 */
export function PipelineFunnel({ stages = [], emptyText }) {
  if (!stages.length || stages.every((s) => num(s.count) === 0)) return <Empty>{emptyText}</Empty>;
  const W = 400, rowH = 34, gap = 4, minW = 0.12;
  const max = Math.max(1, ...stages.map((s) => num(s.count)));
  const widthOf = (n) => (n > 0 ? Math.max(minW, n / max) : minW * 0.5) * W;
  const H = stages.length * (rowH + gap);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12, alignItems: 'center' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
        aria-label={stages.map((s) => `${s.label} ${num(s.count)}`).join(', ')}>
        {stages.map((s, i) => {
          const wTop = widthOf(num(s.count));
          const next = stages[i + 1];
          const wBot = next ? Math.min(wTop, widthOf(num(next.count))) : wTop * 0.85;
          const y = i * (rowH + gap);
          const pts = [
            [(W - wTop) / 2, y], [(W + wTop) / 2, y],
            [(W + wBot) / 2, y + rowH], [(W - wBot) / 2, y + rowH],
          ].map((p) => p.join(',')).join(' ');
          return (
            <g key={s.key || s.label}>
              <polygon points={pts} style={{ fill: s.color || 'var(--accent-primary)', opacity: num(s.count) > 0 ? 1 : 0.35 }}>
                <title>{`${s.label}: ${num(s.count)}`}</title>
              </polygon>
              <text x={W / 2} y={y + rowH / 2 + 5} textAnchor="middle" fontSize="14" fontWeight="700"
                style={{ fill: num(s.count) > 0 ? '#fff' : 'var(--text-secondary)' }}>{num(s.count)}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap }}>
        {stages.map((s) => {
          // "% of previous" hidden by request (all logins):
          // const prev = i > 0 ? num(stages[i - 1].count) : null;
          // const conv = prev ? Math.round((num(s.count) / prev) * 100) : null;
          return (
            <div key={s.key || s.label} style={{ height: rowH, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{s.label}</span>
              {/* {conv != null && <span className="mono text-xs text-muted">{conv}% of previous</span>} */}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One horizontal stacked bar per row (e.g. per consultant).
 * rows: [{ label, parts: { key: value } }], series: [{ key, label, color }]
 */
export function StackedHBars({ rows = [], series = [], emptyText }) {
  if (!rows.length) return <Empty>{emptyText}</Empty>;
  const totals = rows.map((r) => series.reduce((a, s) => a + num(r.parts[s.key]), 0));
  const max = Math.max(1, ...totals);
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 10 }}>
        {series.map((s) => (
          <span key={s.key} className="mono text-xs" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />{s.label}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => (
          <div key={r.key || r.label} style={{ display: 'grid', gridTemplateColumns: 'minmax(80px, 140px) 1fr 36px', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.label}>{r.label}</span>
            <div style={{ display: 'flex', height: 14, background: 'var(--border-subtle)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ display: 'flex', width: `${(totals[i] / max) * 100}%` }}>
                {series.map((s) => {
                  const v = num(r.parts[s.key]);
                  if (!v) return null;
                  return <div key={s.key} title={`${s.label}: ${v}`} style={{ flex: v, background: s.color }} />;
                })}
              </div>
            </div>
            <span className="mono" style={{ fontSize: 12, fontWeight: 600, textAlign: 'right' }}>{totals[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
